import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Clock,
  Camera,
  Cloud,
  Sun,
  CloudRain,
  CloudDrizzle,
  CloudSnow,
  Wind,
  CloudFog,
} from "lucide-react";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import WebcamPlayer from "../../components/WebcamPlayer";
import WebcamSnapshot from "../../components/WebcamSnapshot";
import { webcams } from "../../data/mock";
import "./webcam.css";

/**
 * Redesigned Webcams page (route: /webcam).
 *
 * Visual ground truth: design-ref/public/Webcam.html
 * Functional ground truth: frontend/src/pages/WebcamPage.js
 *  + components/WebcamPlayer.js (HLS click-to-play, 12s stale watchdog,
 *    PREVIEW/LIVE/RECONNECTING/OFFLINE/CONNECTING badges) — imported as-is
 *  + components/WebcamSnapshot.js (60s snapshot polling, "Xs ago" badge)
 *    — imported as-is
 *  + components/WeatherWidget.js — logic ported faithfully into the design's
 *    surface/underground weather cards (same endpoint, 10-min refresh,
 *    fallback + "Est." flag, loading skeleton, BOM disclaimer).
 *
 * Endpoints hit (identical to old page):
 *  - /api/webcams/hutchison/playlist.m3u8 (HLS, after click-to-play)
 *  - /api/webcams/hutchison/snapshot.jpg  (player poster)
 *  - /api/webcams/opalfields/snapshot.jpg (60s poll)
 *  - /api/webcams/rooftop/snapshot.jpg    (60s poll)
 *  - /api/weather/coober-pedy             (on mount + every 10 min)
 */

// Card descriptions copied verbatim from the design file (camera list itself —
// ids, modes, URLs, aspect ratios — stays sourced from data/mock.js).
const DESIGN_COPY = {
  1: "On top of Umoona Opal Mine — the main street of Coober Pedy.",
  2: "Looking out across the opal fields surrounding Coober Pedy.",
  3: "Umoona rooftop, looking out over Coober Pedy.",
};

// Condition → icon mapping ported from components/WeatherWidget.js
const getWeatherIcon = (condition) => {
  if (!condition) return <Sun className="wc-wicon" />;

  const conditionLower = condition.toLowerCase();

  if (conditionLower.includes("rain") || conditionLower.includes("shower")) {
    return <CloudRain className="wc-wicon" />;
  } else if (conditionLower.includes("drizzle")) {
    return <CloudDrizzle className="wc-wicon" />;
  } else if (conditionLower.includes("snow")) {
    return <CloudSnow className="wc-wicon" />;
  } else if (
    conditionLower.includes("cloud") ||
    conditionLower.includes("overcast")
  ) {
    return <Cloud className="wc-wicon" />;
  } else if (conditionLower.includes("fog") || conditionLower.includes("mist")) {
    return <CloudFog className="wc-wicon" />;
  } else if (conditionLower.includes("wind")) {
    return <Wind className="wc-wicon" />;
  } else if (
    conditionLower.includes("clear") ||
    conditionLower.includes("sunny") ||
    conditionLower.includes("fine")
  ) {
    return <Sun className="wc-wicon" />;
  }

  return <Sun className="wc-wicon" />;
};

const RWebcamPage = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [wxLoading, setWxLoading] = useState(true);
  const [wxError, setWxError] = useState(false);

  const handleRefresh = () => setRefreshKey((prev) => prev + 1);

  // Live Adelaide clock — ticks every 30s (old-page behavior)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const fetchWeather = async () => {
    try {
      setWxLoading(true);
      setWxError(false);

      // Fetch weather data from backend proxy (avoids CORS issues)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/weather/coober-pedy`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch weather data");
      }

      const data = await response.json();

      setWeather({
        temp: data.temp,
        apparentTemp: data.apparent_temp,
        condition: data.condition || "Clear",
        humidity: data.humidity,
        windSpeed: data.wind_speed,
        windDir: data.wind_dir,
        observationTime: data.observation_time,
        maxTemp: data.max_temp,
        minTemp: data.min_temp,
      });

      setWxLoading(false);
    } catch (err) {
      console.error("Error fetching weather:", err);
      setWxError(true);
      setWxLoading(false);
      // Set fallback data
      setWeather({
        temp: 25,
        condition: "Sunny",
        humidity: 30,
        windSpeed: 10,
      });
    }
  };

  // Weather on mount + refresh every 10 minutes (WeatherWidget behavior)
  useEffect(() => {
    fetchWeather();
    const interval = setInterval(fetchWeather, 600000);
    return () => clearInterval(interval);
  }, []);

  const adelaideTime = now.toLocaleString("en-AU", {
    timeZone: "Australia/Adelaide",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <MarketingShell pageClass="pg-webcam">
      <SEOHead
        title="Live Coober Pedy Webcams"
        description="View LIVE Webcam and Timelapse video in the Outback town of Coober Pedy in South Australia. Live footage is captured on top of Umoona Opal Mine & Museum."
        keywords="Coober Pedy webcam, live webcam Coober Pedy, outback webcam, Umoona Opal Mine webcam, Coober Pedy weather, South Australia live camera"
        canonical="/webcam"
      />

      {/* HERO — design layout + the old page's live Adelaide clock pill */}
      <div className="u-hero">
        <img className="u-hero__img" src="/design/assets/webcam-live.jpg" alt="Live rooftop webcam view over the Coober Pedy outback from Umoona Opal Mine" />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">● Live from Coober Pedy</div>
          <h1 className="u-hero__title">Live Webcams</h1>
          <p className="u-hero__sub">
            Real-time views from the opal-mining town of Coober Pedy in the
            South Australian outback — streamed 24/7 from the rooftop of Umoona
            Opal Mine &amp; Museum.
          </p>
          <div className="wc-clockpill u-hidesm">
            <div className="wc-clockpill__icon">
              <Clock size={20} />
            </div>
            <div>
              <div className="wc-clockpill__label">Local Time</div>
              <div className="wc-clockpill__time">{adelaideTime}</div>
              <div className="wc-clockpill__tz">Australia/Adelaide</div>
            </div>
          </div>
        </div>
      </div>

      {/* LIVE ROOFTOP CAMERAS */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-eyebrow">Live rooftop cameras</div>
        <h2 className="u-h2">Click any feed to start the live stream</h2>
        <div className="u-grid3 wc-grid">
          {webcams.cameras.map((camera) => (
            <article className="wc-card" key={camera.id}>
              <div className="wc-card__media">
                {camera.mode === "snapshot" ? (
                  <WebcamSnapshot
                    snapshotUrl={camera.snapshotUrl}
                    aspectRatio={camera.aspectRatio}
                    refreshKey={refreshKey}
                    onRefresh={handleRefresh}
                  />
                ) : (
                  <WebcamPlayer
                    streamUrl={camera.streamUrl}
                    posterUrl={camera.posterUrl}
                    aspectRatio={camera.aspectRatio}
                    refreshKey={refreshKey}
                    onRefresh={handleRefresh}
                  />
                )}
              </div>
              <div className="wc-card__body">
                <div className="wc-card__title">{camera.name}</div>
                <div className="wc-card__desc">
                  {DESIGN_COPY[camera.id] || camera.description}
                </div>
                {camera.mode === "snapshot" && (
                  <div className="wc-card__note">
                    <Camera size={13} />
                    <span>Image refreshes every 60 seconds</span>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* WEATHER — surface (live BOM) vs underground (constant) */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">Weather right now</div>
        <h2 className="u-h2">Above ground &amp; below</h2>
        {wxLoading && !weather ? (
          <div className="u-grid2">
            <div className="wc-skel" />
            <div className="wc-skel" />
          </div>
        ) : (
          <>
            <div className="u-grid2">
              <div className="wc-surface">
                <div className="wc-surface__label">
                  {getWeatherIcon(weather?.condition)}
                  <span>COOBER PEDY · SURFACE</span>
                  {wxError && <span className="wc-est">Est.</span>}
                </div>
                <div className="wc-temp">
                  {weather?.temp ? Math.round(weather.temp) : "25"}°C
                </div>
                <div className="wc-surface__cond">
                  {weather?.condition || "Clear"} · updated regularly (Bureau of
                  Meteorology)
                </div>
                {(weather?.apparentTemp ||
                  weather?.humidity ||
                  weather?.windSpeed !== undefined) && (
                  <div className="wc-surface__meta">
                    {weather?.apparentTemp && (
                      <span>Feels like {Math.round(weather.apparentTemp)}°C</span>
                    )}
                    {weather?.humidity && <span>Humidity {weather.humidity}%</span>}
                    {weather?.windSpeed !== undefined && (
                      <span>
                        Wind {weather.windSpeed} km/h {weather?.windDir || ""}
                      </span>
                    )}
                  </div>
                )}
                {weather?.observationTime && (
                  <div className="wc-surface__obs">
                    Measured at {weather.observationTime}
                  </div>
                )}
              </div>
              <div className="wc-underground">
                <div className="wc-underground__label">
                  INSIDE UMOONA · UNDERGROUND
                </div>
                <div className="wc-temp">23.3°C</div>
                <div className="wc-underground__text">
                  Year-round constant — naturally cool &amp; comfortable.
                </div>
              </div>
            </div>
            {!wxError && weather?.observationTime && (
              <p className="wc-disclaimer">
                Weather data from Bureau of Meteorology • Updated regularly
              </p>
            )}
          </>
        )}
      </section>

      {/* CTA */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">Ready to experience it in person?</h2>
          <p className="u-ctacenter__sub">
            The webcams only tell half the story. Come and descend into the
            underground yourself.
          </p>
          <div className="u-ctacenter__btns">
            <Link to="/book-tour" className="u-btn u-btn--primary">
              Book Underground Tour
            </Link>
            <Link to="/coober-pedy" className="u-btn u-btn--ghost">
              About Coober Pedy
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
};

export default RWebcamPage;
